import type { KnowledgeBase } from "@repo/app-types/entities/knowledgeBase";
import {
  MultiSelect,
  type MultiSelectSection,
} from "@repo/ui/Select/multiSelect";
import type { FC, ReactNode } from "react";
import { Link } from "react-router";
import { appPaths } from "../../app/appPaths.js";

export interface KnowledgeBaseSettingsFormProps {
  banner?: ReactNode;
  knowledgeBaseIds: readonly string[];
  allKnowledgeBases: readonly KnowledgeBase[];
  onUpdateKnowledgeBaseIds: (ids: readonly string[]) => void;
  isReadOnly: boolean;
}

const getKnowledgeBaseLabel = (
  knowledgeBaseIds: readonly string[],
  allKnowledgeBases: readonly KnowledgeBase[],
): string | undefined => {
  const count = knowledgeBaseIds.length;
  if (count === 0) return undefined;
  if (count === 1) {
    const selectedKb = allKnowledgeBases.find(
      (kb) => kb.id === knowledgeBaseIds[0],
    );
    return selectedKb?.name ?? "1 knowledge base";
  }
  return `${count} knowledge bases`;
};

export const KnowledgeBaseSettingsForm: FC<KnowledgeBaseSettingsFormProps> = ({
  banner,
  knowledgeBaseIds,
  allKnowledgeBases,
  onUpdateKnowledgeBaseIds,
  isReadOnly,
}) => {
  const knowledgeBaseSections: readonly MultiSelectSection[] = [
    {
      id: "knowledge-bases",
      options: allKnowledgeBases.map((kb) => ({ id: kb.id, label: kb.name })),
    },
  ];

  const hasKnowledgeBases = allKnowledgeBases.length > 0;

  return (
    <div className="space-y-4">
      {banner}

      {hasKnowledgeBases ? (
        <div>
          <MultiSelect
            sections={knowledgeBaseSections}
            value={knowledgeBaseIds}
            onChange={onUpdateKnowledgeBaseIds}
            label={getKnowledgeBaseLabel(knowledgeBaseIds, allKnowledgeBases)}
            placeholder="Select knowledge bases..."
            isDisabled={isReadOnly}
          />
          <p className="mt-2 text-sm text-muted-foreground">
            You can{` `}
            <Link
              to={appPaths.settingsKnowledgeBases.build()}
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              manage knowledge bases
            </Link>{" "}
            in settings.
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-border-strong p-4 text-center">
          <p className="text-sm text-muted-foreground">
            No knowledge bases configured yet.
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            <Link
              to={appPaths.settingsKnowledgeBases.build()}
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              Go to Settings
            </Link>{" "}
            to create your first knowledge base.
          </p>
        </div>
      )}
    </div>
  );
};
